import {withState} from 'recompose'

import compose from 'lodash/flowRight'
import throttle from 'lodash/throttle'
import * as React from 'react'

import {withContentRect} from '../../../containers/Measure'
import withHook from '../../../hocs/withHook'
import type {BuildId} from '../../../types'
import {closest, matches} from '../../../utils/dom'

import type {OwnProps, ForwardedProps, Props, Offset} from './BuildTypeInfoWrapper.types'

const BOTTOM_PADDING = 12
const MOUSE_MOVE_TIMEOUT = 50

class BuildTypeInfoWrapper extends React.PureComponent<Props> {
  lastHovered: BuildId | null | undefined
  vertexOffset: Offset | null | undefined
  isBlocked = false
  hoverTimeout?: number
  handleMouseMove = (e: React.MouseEvent<any>) => {
    clearTimeout(this.hoverTimeout)
    if (this.shouldBlock(e)) {
      this.scheduleHover(e)
      return
    }

    if (this.isBlocked) {
      this.unblock()
      this.hoverBuild(this.lastHovered, e)
    } else if (this.lastHovered != null) {
      // Update vertex offset if we're moving inside the bar
      this.vertexOffset = this.getOffset(e)
    }
  }

  handleMouseLeave = (e: React.MouseEvent<any>) => {
    this.props.onMouseLeave()
    this.unblock()
    this.hoverBuild(null, e)
  }

  handleHoverBuild = (id: BuildId | null | undefined, e: React.MouseEvent<any>) => {
    clearTimeout(this.hoverTimeout)
    this.lastHovered = id

    if (this.shouldBlock(e)) {
      this.isBlocked = true
      this.scheduleHover(e)
    } else {
      this.hoverBuild(id, e)
    }
  }

  unblock = () => {
    this.isBlocked = false
    this.vertexOffset = null
  }

  scheduleHover(e: React.MouseEvent) {
    this.hoverTimeout = window.setTimeout(() => {
      this.unblock()
      if (this.lastHovered != null) {
        this.hoverBuild(this.lastHovered, e)
      }
    }, MOUSE_MOVE_TIMEOUT * 2)
  }

  hoverBuild(id: BuildId | null | undefined, e: React.MouseEvent<any>) {
    this.props.hoverBuild(id)
    this.vertexOffset = id != null ? this.getOffset(e) : null
  }

  shouldBlock(e: React.MouseEvent<any>): boolean {
    const offset = this.getOffset(e)

    if (offset == null) {
      return false
    }

    const {vertexOffset} = this

    if (vertexOffset == null) {
      return false
    }

    const movedRight = (vertexOffset.right - offset.right) / vertexOffset.right
    const movedDown = (vertexOffset.bottom - offset.bottom) / vertexOffset.bottom
    const movedLeft = (vertexOffset.left - offset.left) / vertexOffset.left
    return movedLeft <= movedDown && movedRight <= movedDown
  }

  getOffset({clientX, clientY}: React.MouseEvent<any>): Offset | null | undefined {
    const {bounds} = this.props.contentRect

    if (bounds == null) {
      return null
    }

    const {top = 0, right = 0, left = 0} = bounds
    return {
      right: right - clientX,
      bottom: top - BOTTOM_PADDING - clientY,
      left: clientX - left,
    }
  }

  render() {
    const {
      children,
      hoveredBuildId,
      selectedBuildId,
      selectBuild,
      hoverBuild,
      measureRef,
      contentRect,
      hover,
      onMouseEnter,
    } = this.props
    const overallProps: ForwardedProps = {
      contentRect,
      hoveredBuildId,
      selectedBuildId,
      selectBuild,
      hoverBuild,
      measureRef,
      hover,
      handleMouseEnter: onMouseEnter,
      handleHoverBuild: this.handleHoverBuild,
      handleMouseLeave: this.handleMouseLeave,
      handleMouseMove: throttle(this.handleMouseMove, MOUSE_MOVE_TIMEOUT),
    }
    return React.cloneElement(React.Children.only(children), overallProps)
  }
}

const UNHOVER_DELAY = 150 // ms

const BuildTypeInfoWrapperContainer: React.ComponentType<OwnProps> = compose(
  withState('selectedBuildId', 'selectBuild', undefined),
  withState('hoveredBuildId', 'hoverBuild', undefined),
  withContentRect(['scroll', 'bounds']),
  withHook(() => {
    const [hover, setHover] = React.useState(false)
    const unhoverTO = React.useRef<number | null>()
    const onMouseEnter = React.useCallback(() => {
      setHover(true)

      if (unhoverTO.current != null) {
        window.clearTimeout(unhoverTO.current)
        unhoverTO.current = null
      }
    }, [setHover, unhoverTO])
    const onMouseLeave = React.useCallback(() => {
      unhoverTO.current = window.setTimeout(() => setHover(false), UNHOVER_DELAY)
    }, [setHover, unhoverTO])
    React.useEffect(() => {
      if (hover) {
        const selector = '.popupDiv'

        const onGlobalMouseOver = (e: Event) => {
          if (closest(e.target, selector) != null) {
            onMouseEnter()
          }
        }

        const onGlobalMouseOut = (e: Event) => {
          if (matches(e.target, selector)) {
            onMouseLeave()
          }
        }

        document.addEventListener('mouseover', onGlobalMouseOver, true)
        document.addEventListener('mouseout', onGlobalMouseOut, true)
        return () => {
          document.removeEventListener('mouseover', onGlobalMouseOver, true)
          document.removeEventListener('mouseout', onGlobalMouseOut, true)
        }
      }

      return undefined
    }, [hover, onMouseEnter, onMouseLeave])
    return {
      hover,
      onMouseEnter,
      onMouseLeave,
    }
  }),
)(BuildTypeInfoWrapper)
export default BuildTypeInfoWrapperContainer
